import { useMemo, useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type AdminUser, type BirthDateChange, type UserBirthDate } from '../lib/api.js';
import {
  MONTH_OPTIONS,
  TICKET_REFERENCE_MAX,
  longDate,
  partsOf,
  planCorrection,
  becomesMinor,
  readback,
  sourceLabel,
  type BirthDateParts,
} from '../lib/birth-date.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import { usePersonName } from '../lib/people.js';
import { useAction, useAsync } from '../lib/useAsync.js';
import { AgeBadge } from '../components/AgeBand.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Select } from '../components/Select.js';
import { InfoBubble } from '../components/Screen.js';
import {
  AsyncBoundary,
  Button,
  EmptyState,
  Field,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';
import { Fact, factsStyle } from './UserDetail.js';

/**
 * The member's date of birth, and support's correction of it (RidgeWill/auth#672).
 *
 * Absent for a role without `birthDate:read` rather than disabled: the whole
 * panel is one read of a sensitive field. The band is on every tab already
 * (the header badge); this is where the date itself, where it came from and
 * every change to it are.
 *
 * ── THE TICKET REFERENCE IS TEXT, ON PURPOSE ─────────────────────────
 *
 * Support corrects a date after checking a document sent on a ticket, and
 * the reference says which one. It is stored as typed and never checked
 * against support: a link between the two records would be a reason a ticket
 * (and the ID photo on it) could not be deleted, which is the opposite of
 * what the proof-retention rule wants.
 */
export function BirthDatePanel({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const mayRead = useCan('birthDate:read');
  const mayCorrect = useCan('birthDate:correct');
  const { state, reload } = useAsync(
    () => (mayRead ? adminApi.userBirthDate(user.id) : Promise.resolve(null)),
    [user.id, mayRead],
  );
  const [open, setOpen] = useState(false);
  if (!mayRead) return null;

  return (
    <Panel
      title="Date of birth"
      info={
        <InfoBubble label="What this covers">
          Apps never see the date, only the age group it gives. A correction here applies to apps within 15
          minutes, when their access token next refreshes. An app store age signal, when one is on file,
          decides the age group instead of this date.
        </InfoBubble>
      }
      action={
        <Button
          disabled={!mayCorrect || state.status !== 'ready'}
          title={mayCorrect ? undefined : whyDisabled('correcting a date of birth')}
          onClick={() => setOpen(true)}
        >
          Correct date of birth
        </Button>
      }
    >
      <AsyncBoundary state={state}>
        {(d) =>
          d ? (
            <>
              {open ? (
                <CorrectBirthDate
                  userId={user.id}
                  current={d.dateOfBirth}
                  onClose={() => setOpen(false)}
                  onSaved={() => {
                    setOpen(false);
                    reload(true);
                    onChanged();
                  }}
                />
              ) : null}
              <PanelBody>
                <div style={factsStyle}>
                  <Fact label="Date on file">
                    {d.dateOfBirth ? longDate(d.dateOfBirth) : <span style={{ color: C.muted }}>none given</span>}
                  </Fact>
                  <Fact label="Age group">
                    {d.ageBand ? <AgeBadge band={d.ageBand} /> : <span style={{ color: C.muted }}>none</span>}
                  </Fact>
                  <Fact label="Source">{d.source ? sourceLabel(d.source) : <span style={{ color: C.muted }}>none</span>}</Fact>
                  <Fact label="Set">
                    {d.setAt ? <When iso={d.setAt} /> : <span style={{ color: C.muted }}>never</span>}
                  </Fact>
                </div>
              </PanelBody>
              <History data={d} />
            </>
          ) : null
        }
      </AsyncBoundary>
    </Panel>
  );
}

function History({ data }: { data: UserBirthDate }) {
  if (!data.history) return null;
  if (data.history.length === 0) {
    return <EmptyState title="No changes recorded" suggestion="Changes are listed here once the date is given or changed." />;
  }
  return (
    <Table columns={['When', 'Change', 'Source', 'By', 'Ticket reference']}>
      {data.history.map((h, i) => (
        <HistoryRow key={`${h.createdAt}-${i}`} change={h} />
      ))}
    </Table>
  );
}

function HistoryRow({ change: h }: { change: BirthDateChange }) {
  return (
    <tr>
      <Td>
        <When iso={h.createdAt} />
      </Td>
      <Td>
        {h.previousDate ? (
          <>
            <span style={{ color: C.muted }}>{longDate(h.previousDate)}</span> {'→'} {longDate(h.newDate)}
          </>
        ) : (
          <>First given: {longDate(h.newDate)}</>
        )}
      </Td>
      <Td>{sourceLabel(h.source)}</Td>
      <Td>
        <SetBy id={h.setById} source={h.source} />
      </Td>
      <Td>{h.ticketReference ? <span style={{ overflowWrap: 'anywhere' }}>{h.ticketReference}</span> : <span style={{ color: C.muted }}>none</span>}</Td>
    </tr>
  );
}

/** Staff by name and linked; a member's own change has no setter and reads as the member. */
function SetBy({ id, source }: { id: string | null; source: string }) {
  const name = usePersonName(id);
  if (!id) return <span style={{ color: C.muted }}>{source === 'support' ? 'Unknown' : 'The member'}</span>;
  return (
    <a href={`#/users/${encodeURIComponent(id)}`} style={{ color: C.brand, fontWeight: 600, textDecoration: 'none' }}>
      {name ?? id.slice(0, 8)}
    </a>
  );
}

/**
 * The correction dialog. Its own component so the draft is discarded with it,
 * like Add a note.
 */
function CorrectBirthDate({
  userId,
  current,
  onClose,
  onSaved,
}: {
  userId: string;
  current: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [parts, setParts] = useState<BirthDateParts>(() => partsOf(current));
  const [ticketReference, setTicketReference] = useState('');
  const action = useAction();
  const today = useMemo(() => new Date(), []);
  const line = readback(parts, today);
  const plan = planCorrection(parts, ticketReference, current, today);
  const set = (key: keyof BirthDateParts) => (next: string) => setParts((p) => ({ ...p, [key]: next }));

  return (
    <Modal
      title="Correct date of birth"
      subtitle="Use this after checking a state-issued ID or driver's license, a passport, or a birth certificate."
      onClose={onClose}
      width={520}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !plan.ok}
            title={plan.ok ? undefined : plan.reason}
            onClick={() =>
              plan.ok &&
              void action
                .run(async () => {
                  const r = await adminApi.correctBirthDate(userId, plan.body);
                  return r.unfrozen ? 'Saved. The account was frozen and is now unfrozen.' : 'Saved.';
                })
                .then((ok) => {
                  if (ok) onSaved();
                })
            }
          >
            {action.running ? 'Saving…' : 'Save'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: '.4rem' }}>
        <legend style={st.legend}>Date of birth</legend>
        <div style={st.row}>
          <span style={st.part}>
            <span id="dob-month-label">Month</span>
            <Select
              value={parts.month}
              options={MONTH_OPTIONS}
              onChange={set('month')}
              labelledBy="dob-month-label"
              placeholder="Month"
              block
            />
          </span>
          <span style={st.part}>
            <label htmlFor="dob-day">Day</label>
            <input
              id="dob-day"
              inputMode="numeric"
              maxLength={2}
              value={parts.day}
              onChange={(e) => set('day')(e.target.value.replace(/\D/g, ''))}
              style={inputStyle}
            />
          </span>
          <span style={st.part}>
            <label htmlFor="dob-year">Year</label>
            <input
              id="dob-year"
              inputMode="numeric"
              maxLength={4}
              value={parts.year}
              onChange={(e) => set('year')(e.target.value.replace(/\D/g, ''))}
              style={inputStyle}
            />
          </span>
        </div>
        <p aria-live="polite" style={{ ...st.readback, color: line.kind === 'ok' ? C.text : C.warnText }}>
          {line.kind === 'empty' ? ' ' : line.text}
        </p>
      </fieldset>
      <Field
        label="Ticket reference (optional)"
        hint="The ticket number, as plain text. It is saved with the change and is not linked to the ticket."
      >
        <input
          value={ticketReference}
          maxLength={TICKET_REFERENCE_MAX}
          onChange={(e) => setTicketReference(e.target.value)}
          placeholder="e.g. SUP-1042"
          style={inputStyle}
        />
      </Field>
      {line.kind === 'ok' && becomesMinor(current, line.iso, today) ? (
        <Note tone="warn">This date makes the member under 18. Protections for members under 18 apply right away.</Note>
      ) : null}
    </Modal>
  );
}

const st = {
  legend: { padding: 0, marginBottom: '.45rem', fontSize: '.85rem', fontWeight: 600, color: C.textMid },
  row: { display: 'grid', gridTemplateColumns: '2fr 1fr 1.3fr', gap: '.5rem' },
  part: { display: 'grid', gap: '.25rem', fontSize: '.8rem', color: C.textMid, minWidth: 0 },
  readback: { margin: 0, fontSize: '.88rem', minHeight: '1.3em' },
} as const;
